import * as m from '@staiter/i18n';
import type { BudgetVarianceLine } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Breadcrumbs, PageHeader, Table, type TableColumn } from '~/components/data';
import { AIInsightCard, TierApprovalDialog, VarianceBarChart } from '~/components/domain';
import { Button } from '~/components/primitives';
import {
  activateBudget,
  archiveBudget,
  fetchBudget,
  fetchBudgetApprovers,
  fetchBudgetVariance,
  lockBudget,
} from '~/lib/api/budgets';
import { formatMoney } from '~/lib/format';
import { formatMoneyCompact } from '~/lib/format/money';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.budgets.$id.module.css';

/**
 * S16-FE.2 + S16-FE.3 — Budget detail + variance tab.
 */

export const Route = createFileRoute('/_auth/budgets/$id/')({
  staticData: { minMainWidth: 960 },
  component: BudgetDetailPage,
  // FE-300 Pass 67: validate search params для URL-bindable tab state.
  validateSearch: (search: Record<string, unknown>): { tab?: 'overview' | 'variance' } => ({
    tab: search.tab === 'variance' || search.tab === 'overview' ? search.tab : undefined,
  }),
});

const FLAG_COLORS: Record<string, string> = {
  ok: 'var(--color-success, #2e7d32)',
  warning: 'var(--color-warning, #ed6c02)',
  critical: 'var(--color-error, #c62828)',
};

function BudgetDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  // FE-300 Pass 67: tab state from URL search params (URL-bindable).
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab: 'overview' | 'variance' = search.tab ?? 'overview';
  const setTab = (next: 'overview' | 'variance'): void => {
    void navigate({ search: { tab: next === 'overview' ? undefined : next } });
  };
  const [dialog, setDialog] = useState<null | 'activate' | 'lock'>(null);
  const scope = useUserScope();
  // BE-10 (tails-sweep-2): `budget` заведён в каноне ABILITIES — прокси через
  // `sales_order` снят, право на бюджет больше не выводится из права на заказ.
  const canEdit = canDo(scope, 'budget', 'update');
  const qc = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id ?? '');

  const budgetQuery = useQuery({ queryKey: ['budgets', id], queryFn: () => fetchBudget(id) });
  const varianceQuery = useQuery({
    queryKey: ['budgets', id, 'variance'],
    queryFn: () => fetchBudgetVariance(id),
    enabled: tab === 'variance',
  });

  /*
   * PLM-4 (пак `tails-closeout`, B17 (б)) — подписант, выбранный в диалоге, теперь
   * уходит на сервер. Раньше диалог его собирал, а страница выбрасывала: активация и
   * блокировка шли без подписи.
   */
  const approversQuery = useQuery({
    queryKey: ['budgets', 'approvers', dialog],
    queryFn: () => fetchBudgetApprovers(dialog === 'lock' ? 'lock' : 'activate'),
    enabled: dialog !== null,
  });
  const approverCandidates = (approversQuery.data?.items ?? []).map((a) => ({
    id: a.id,
    label: a.full_name ? `${a.full_name} · ${a.email}` : a.email,
  }));
  const activateMut = useMutation({
    mutationFn: (approverId: string) => activateBudget(id, approverId),
    onSuccess: () => {
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
  const lockMut = useMutation({
    mutationFn: (approverId: string) => lockBudget(id, approverId),
    onSuccess: () => {
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
  const archiveMut = useMutation({
    mutationFn: () => archiveBudget(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['budgets'] }),
  });

  const varianceColumns: TableColumn<BudgetVarianceLine>[] = useMemo(
    () => [
      {
        id: 'acc',
        header: m.variance_column_account(),
        accessor: (r) => `${r.account_code} · ${r.account_name_ru}`,
        sticky: true,
      },
      {
        id: 'plan',
        header: m.variance_column_plan(),
        accessor: (r) => formatMoney(r.plan_amount),
        mono: true,
        align: 'right',
      },
      {
        id: 'actual',
        header: m.variance_column_actual(),
        accessor: (r) => formatMoney(r.actual_amount),
        mono: true,
        align: 'right',
      },
      {
        id: 'diff',
        header: m.variance_column_diff(),
        accessor: (r) => formatMoney(r.variance_amount),
        mono: true,
        align: 'right',
      },
      {
        id: 'pct',
        header: m.variance_column_pct(),
        accessor: (r) => `${r.variance_pct.amount}%`,
        mono: true,
        align: 'right',
      },
      {
        id: 'flag',
        header: m.variance_column_flag(),
        accessor: (r) => (
          // staiter-inline-style-ok: dynamic color per flag value.
          <span style={{ color: FLAG_COLORS[r.flag] ?? 'inherit' }}>
            {r.flag === 'ok'
              ? m.variance_flag_ok()
              : r.flag === 'warning'
                ? m.variance_flag_warning()
                : m.variance_flag_critical()}
          </span>
        ),
        width: '100px',
      },
    ],
    [],
  );

  const b = budgetQuery.data;
  if (!b) {
    return (
      <div>
        <PageHeader title={m.phase2_budgets_title()} />
        <div>{budgetQuery.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: m.phase2_budgets_title(), to: '/budgets' }, { label: b.name_ru }]}
          />
        }
        title={b.name_ru}
        actions={
          <>
            {b.state === 'draft' && canEdit && (
              <Link to="/budgets/$id/edit" params={{ id }}>
                <Button variant="secondary">{m.common_button_edit()}</Button>
              </Link>
            )}
            {b.state === 'draft' && (
              <Button onClick={() => setDialog('activate')}>{m.budget_button_activate()}</Button>
            )}
            {b.state === 'active' && (
              <>
                {/* FE-97: clone-to-draft action для active budget — позволяет редактировать
                    структуру через создание новой версии-draft без unlock active budget. */}
                <Link to="/budgets/new" search={{ clone_from: id }}>
                  <Button variant="ghost">{m.ui_kopiya_chernovik()}</Button>
                </Link>
                <Button onClick={() => setDialog('lock')}>{m.budget_button_lock()}</Button>
              </>
            )}
            {(b.state === 'locked' || b.state === 'draft') && (
              <Button
                variant="ghost"
                onClick={() => archiveMut.mutate()}
                loading={archiveMut.isPending}
              >
                {m.budget_button_archive()}
              </Button>
            )}
          </>
        }
      />

      <TierApprovalDialog
        open={dialog === 'activate'}
        tier="tier_2"
        taskType="budget_activate"
        titleOverride={m.budget_button_activate()}
        currentUserId={currentUserId}
        onClose={() => setDialog(null)}
        onSubmit={async ({ approvers }) => await activateMut.mutateAsync(approvers[0] ?? '')}
        loading={activateMut.isPending}
        candidates={approverCandidates}
      />
      <TierApprovalDialog
        open={dialog === 'lock'}
        tier="tier_2"
        taskType="budget_lock"
        titleOverride={m.budget_button_lock()}
        currentUserId={currentUserId}
        onClose={() => setDialog(null)}
        onSubmit={async ({ approvers }) => await lockMut.mutateAsync(approvers[0] ?? '')}
        loading={lockMut.isPending}
        candidates={approverCandidates}
      />

      <div role="tablist" className={styles.tabs}>
        <Button
          variant={tab === 'overview' ? 'primary' : 'ghost'}
          onClick={() => setTab('overview')}
        >
          {m.budget_tab_overview()}
        </Button>
        <Button
          variant={tab === 'variance' ? 'primary' : 'ghost'}
          onClick={() => setTab('variance')}
        >
          {m.budget_tab_variance()}
        </Button>
      </div>

      {tab === 'overview' ? (
        <div className={styles.statsGrid}>
          <div className={shared.elevatedCard}>
            <div className={shared.statLabel}>{m.budget_column_revenue_plan()}</div>
            <div>
              {/* FE-299 Pass 65: compact формат для KPI больших значений. */}
              <strong data-testid="budget-revenue-plan">
                {formatMoneyCompact(b.total_revenue_plan)}
              </strong>
            </div>
          </div>
          <div className={shared.elevatedCard}>
            <div className={shared.statLabel}>{m.budget_column_cost_plan()}</div>
            <div>
              <strong data-testid="budget-cost-plan">
                {formatMoneyCompact(b.total_cost_plan)}
              </strong>
            </div>
          </div>
          <div
            className={[
              shared.elevatedCard,
              b.total_profit_plan.amount.startsWith('-')
                ? styles.profitCardNegative
                : styles.profitCardPositive,
            ].join(' ')}
          >
            <div className={shared.statLabel}>{m.pnl_net_profit()}</div>
            <div>
              <strong data-testid="budget-profit-plan">
                {formatMoneyCompact(b.total_profit_plan)}
              </strong>
            </div>
          </div>
          {/* FE-98: AI Insight Card для catastrophic planned loss. Расчёт margin
              на FE — Decimal-safe через string parse (negative prefix + ratio). */}
          {b.total_profit_plan.amount.startsWith('-') &&
            (() => {
              const lossAbs = Math.abs(Number(b.total_profit_plan.amount));
              const revenue = Number(b.total_revenue_plan.amount) || 1;
              const marginPct = (-lossAbs / revenue) * 100;
              if (marginPct > -10) return null; // только если убыток > 10% выручки
              return (
                <div style={{ gridColumn: '1 / -1' }}>
                  <AIInsightCard
                    title="Анализ бюджета"
                    body={
                      <span>
                        Запланирован убыток{' '}
                        <strong>{formatMoneyCompact(b.total_profit_plan)}</strong> при выручке{' '}
                        <strong>{formatMoneyCompact(b.total_revenue_plan)}</strong> —{' '}
                        <strong>{marginPct.toFixed(1)}%</strong> маржа. Рекомендуется проверить
                        cost-структуру до активации.
                      </span>
                    }
                  />
                </div>
              );
            })()}
        </div>
      ) : (
        <>
          {varianceQuery.data && <VarianceBarChart lines={varianceQuery.data.lines} />}
          <Table<BudgetVarianceLine>
            data={varianceQuery.data?.lines ?? []}
            columns={varianceColumns}
            rowKey={(r) => r.account_code}
            ariaLabel={m.budget_tab_variance()}
            loading={varianceQuery.isLoading}
            emptyTitle={m.budget_empty_title()}
          />
        </>
      )}
      {/* EFE-13: last-modified footer with audit log link */}
      <div className={shared.subtle} style={{ marginTop: 'var(--space-3)' }}>
        {m.detail_last_updated()}: {String(b.updated_at ?? b.created_at).slice(0, 10)}
        {' · '}
        <a href={`/admin/audit?entity_id=${id}`} className={shared.link}>
          {m.detail_view_audit_log()}
        </a>
      </div>
    </div>
  );
}
